import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Container,
  Typography,
  Grid,
  Box,
  Button,
  Divider,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  alpha,
} from "@mui/material";
import {
  ExpandMore,
  Help,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Info,
  QuestionAnswer,
  ContactSupport,
  Update,
  VerifiedUser,
  LocationOn,
  Search,
  DataUsage,
  Feedback,
  School,
  Map,
  Home,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import HomeRefreshButton from "../buttons/HomeRefreshButton";

const Faq = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expanded, setExpanded] = useState(null);

  const faqItems = [
    {
      question: "Всички читалища в България ли са включени в картата?",
      answer:
        "В Първата онлайн карта на народните читалища са включени всички читалища, които са вписани в Публичния регистър на народните читалища и на читалищните сдружения, управляван и поддържан от Министерството на културата. Допълнително условие е читалището да има посочен БУЛСТАТ или ЕИК номер. На тези условия, към момента на изготвяне на картата, отговарят 3597 читалища от цяла България.",
      icon: <CheckCircle />,
      iconColor: "#2e7d32",
    },
    {
      question: "Има ли читалища, които не са включени в картата?",
      answer:
        "Да. В картата не са включени около 170 читалища, които присъстват в Публичния регистър на народните читалища и на читалищните сдружения, поддържан от Министерството на културата. Това са читалища, за които в регистъра липсва почти цялата основна информация – няма посочен БУЛСТАТ или ЕИК номер, не са подавани годишни информационни карти, а в много случаи липсват и адреси или контакти за връзка. За малка част от тези читалища в регистъра е отбелязано, че са закрити или недействащи.",
      icon: <Warning />,
      iconColor: "#ed6c02",
    },
    {
      question:
        "Можем ли от тази карта да разберем кои читалища в България са активни и кои нямат дейност?",
      answer:
        "Не. Първата онлайн карта на народните читалища няма за цел да определя кои читалища са активни и кои не. Подобно заключение не може да бъде направено единствено на база на наличните данни, тъй като информацията в Публичния регистър на народните читалища и на читалищните сдружения е непълна и неравномерно актуализирана.",
      icon: <ErrorIcon />,
      iconColor: "#d32f2f",
    },
    {
      question:
        "Точките на картата посочват ли точното разположение на читалищата?",
      answer:
        "Не винаги. Част от точките показват точното местоположение на читалищата. За други обаче липсва точен адрес или посоченият адрес не може да бъде еднозначно открит на картата. В такива случаи локацията е поставена близо до предполагаемото местонахождение на читалището или в центъра на населеното място. Това най-често се отнася за селата, където сградите на читалищата обичайно се намират в централната част.",
      icon: <LocationOn />,
      iconColor: "#1976d2",
    },
    {
      question:
        "Как е събрана информацията за дейността на всяко читалище? Достоверна ли е тя?",
      answer:
        'Всички данни, представени в индивидуалните страници на читалищата, са извлечени от Публичния регистър на народните читалища и на читалищните сдружения, поддържан от Министерството на културата. Тази информация се подава от самите читалища чрез техните годишни информационни карти. Фондация „Народни читалища" не може да потвърди точността и пълнотата на данните, подадени от всяко отделно читалище.',
      icon: <VerifiedUser />,
      iconColor: "#9c27b0",
    },
    {
      question: "Защо за някои читалища няма информация за дейността им?",
      answer:
        "Когато за дадено читалище липсва информация за дейността му или за определени години, това означава, че читалището не е подало своите годишни информационни карти за съответния период в Публичния регистър.",
      icon: <Search />,
      iconColor: "#1976d2",
    },
    {
      question:
        "Защо последната налична информация за дейността на читалищата е от 2023 г.?",
      answer:
        "В картата е представена последната налична информация от Публичния регистър на народните читалища и на читалищните сдружения. Към декември 2025 г. регистърът не е актуализиран повече от година, поради което читалищата не са имали възможност да подадат информационните си карти за 2024 г. и тези данни не са налични.",
      icon: <Update />,
      iconColor: "#ed6c02",
    },
    {
      question: "Как сте проверили информацията за дейността на читалищата?",
      answer:
        'Информацията за дейността на читалищата се подава от самите читалища, както е предвидено в Закона за народните читалища и Наредба № 2 от 1.07.2019 г. за реда за водене на регистъра на народните читалища и на читалищните сдружения. Фондация „Народни читалища" не извършва съдържателна проверка на всяко отделно подадено сведение и не може да гарантира неговата точност.',
      icon: <DataUsage />,
      iconColor: "#455a64",
    },
    {
      question: "Какво да направя, ако открия грешна или непълна информация?",
      answer:
        'Ако откриете грешна или непълна информация, моля информирайте ни чрез формата в раздела „Обратна връзка". Всички сигнали се разглеждат и се използват за подобряване на качеството на данните в картата.',
      icon: <Feedback />,
      iconColor: "#1976d2",
    },
    {
      question: "Ще има ли корекции и как ще се развива картата занапред?",
      answer:
        'Това е версия 1.0 на Първата онлайн карта на народните читалища в България. Възможно е да има неточности или непълна информация, които ще бъдат коригирани постепенно. Картата ще продължи да се развива и обогатява с нови данни и функционалности, в зависимост от наличните ресурси и възможности на Фондация „Народни читалища".',
      icon: <Update />,
      iconColor: "#2e7d32",
    },
    {
      question: "Кой поддържа и обновява картата?",
      answer:
        'Картата се поддържа от Фондация „Народни читалища", която отговаря за събирането, обработката и визуализацията на данните, както и за тяхното постепенно подобряване въз основа на официални източници и получена обратна връзка.',
      icon: <School />,
      iconColor: "#9c27b0",
    },
    {
      question:
        "Могат ли читалищата сами да коригират информацията си в картата?",
      answer:
        'Към момента читалищата не могат директно да редактират информацията си в картата. Корекциите и допълненията се извършват след подаден сигнал чрез раздела „Обратна връзка" и последваща проверка на предоставената информация.',
      icon: <ContactSupport />,
      iconColor: "#ed6c02",
    },
    {
      question:
        "Мога ли да използвам данните от картата за изследване или публикация?",
      answer:
        "Да. Данните от картата могат да бъдат използвани за изследователски, аналитични, образователни и журналистически цели. При използване на информацията е препоръчително ясно да се посочва източникът – Първата онлайн карта на народните читалища в България, както и първичният източник на данните – Публичният регистър на народните читалища и на читалищните сдружения към Министерството на културата. При интерпретация на данните следва да се има предвид, че те отразяват наличната информация към съответния период и могат да съдържат непълноти.",
      icon: <Info />,
      iconColor: "#1976d2",
    },
  ];

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : null);
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Заглавие */}
        <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
          <QuestionAnswer
            sx={{
              fontSize: { xs: 48, md: 60 },
              color: "#1976d2",
              mb: 2,
            }}
          />
          <Typography
            variant={isMobile ? "h4" : "h3"}
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: "#1a237e",
              mb: 3,
              textAlign: "center",
            }}
          >
            Често задавани въпроси
          </Typography>
          <Typography
            variant={isMobile ? "body1" : "h6"}
            sx={{
              color: "#455a64",
              maxWidth: "800px",
              margin: "0 auto",
              mb: 4,
              textAlign: { xs: "left", md: "center" },
              lineHeight: 1.6,
            }}
          >
            Отговори на най-често задаваните въпроси за Първата онлайн карта на
            народните читалища
          </Typography>
          <Divider
            sx={{
              my: 4,
              mx: "auto",
              width: { xs: "60px", md: "100px" },
              borderWidth: 2,
              borderColor: "#1976d2",
            }}
          />
        </Box>

        {/* Важна информация */}
        <Alert
          severity="info"
          icon={<Info />}
          sx={{
            mb: { xs: 4, md: 6 },
            borderRadius: 3,
            "& .MuiAlert-icon": {
              fontSize: { xs: 24, md: 30 },
              alignItems: "center",
            },
          }}
        >
          <Typography
            variant="body1"
            sx={{ textAlign: "justify", hyphens: "auto" }}
          >
            Този раздел съдържа отговори на най-често задаваните въпроси относно
            картата, източниците на данни, точността на информацията и бъдещото
            развитие на платформата.
          </Typography>
        </Alert>

        {/* Статистика */}
        <Card
          elevation={2}
          sx={{
            mb: { xs: 4, md: 6 },
            borderRadius: 3,
            bgcolor: "background.paper",
          }}
        >
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography
                  variant={isMobile ? "subtitle1" : "h6"}
                  sx={{
                    mb: 2,
                    color: "#1a237e",
                    fontWeight: 600,
                  }}
                >
                  Ключова информация:
                </Typography>
                <List dense>
                  <ListItem sx={{ px: 0, alignItems: "flex-start" }}>
                    <ListItemIcon sx={{ minWidth: 36, mt: 0.25 }}>
                      <CheckCircle sx={{ color: "#2e7d32" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="3597 читалища включени в картата"
                      primaryTypographyProps={{
                        variant: "body2",
                        textAlign: "justify",
                      }}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0, alignItems: "flex-start" }}>
                    <ListItemIcon sx={{ minWidth: 36, mt: 0.25 }}>
                      <Warning sx={{ color: "#ed6c02" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="~170 читалища невключени поради липса на данни"
                      primaryTypographyProps={{
                        variant: "body2",
                        textAlign: "justify",
                      }}
                    />
                  </ListItem>
                  <ListItem sx={{ px: 0, alignItems: "flex-start" }}>
                    <ListItemIcon sx={{ minWidth: 36, mt: 0.25 }}>
                      <Update sx={{ color: "#1976d2" }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Версия 1.0 – постоянно развитие и актуализации"
                      primaryTypographyProps={{
                        variant: "body2",
                        textAlign: "justify",
                      }}
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography
                  variant={isMobile ? "subtitle1" : "h6"}
                  sx={{
                    mb: 2,
                    color: "#1a237e",
                    fontWeight: 600,
                  }}
                >
                  Източник на данни:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#455a64",
                    mb: 3,
                    textAlign: "justify",
                    hyphens: "auto",
                  }}
                >
                  Публичен регистър на народните читалища и на читалищните
                  сдружения към Министерството на културата
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip
                    label="Официален източник"
                    color="primary"
                    variant="outlined"
                    size={isMobile ? "small" : "medium"}
                    sx={{
                      mr: 1,
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "primary.main",
                      },
                    }}
                    component="a"
                    href="https://chitalishta.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    clickable
                  />

                  <Chip
                    label="Обществен достъп"
                    href="https://bit.ly/chitalishta-master-table"
                    color="info"
                    variant="outlined"
                    size={isMobile ? "small" : "medium"}
                    component="a"
                    target="_blank"
                    rel="noopener noreferrer"
                    clickable
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* FAQ акордеони */}
        <Box sx={{ mb: { xs: 4, md: 6 } }}>
          {faqItems.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              whileHover={{ y: -2 }}
            >
              <Accordion
                expanded={expanded === `panel${index}`}
                onChange={handleChange(`panel${index}`)}
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  overflow: "hidden",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                  "&:before": { display: "none" },
                  transition: "all 0.3s ease",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    bgcolor:
                      expanded === `panel${index}`
                        ? alpha(item.iconColor, 0.08)
                        : "background.paper",
                    borderLeft: `4px solid ${item.iconColor}`,
                    py: { xs: 1.5, md: 2 },
                    px: { xs: 2, md: 3 },
                    transition: "all 0.3s ease",
                    "& .MuiAccordionSummary-expandIconWrapper": {
                      color: item.iconColor,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      width: "100%",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        color: item.iconColor,
                        display: "flex",
                        alignItems: "center",
                        flexShrink: 0,
                        mt: 0.25,
                      }}
                    >
                      {React.cloneElement(item.icon, {
                        fontSize: isMobile ? "small" : "medium",
                      })}
                    </Box>
                    <Typography
                      variant={isMobile ? "subtitle1" : "h6"}
                      sx={{
                        fontWeight: 600,
                        color:
                          expanded === `panel${index}`
                            ? item.iconColor
                            : "text.primary",
                        fontSize: { xs: "0.9rem", md: "1rem" },
                        textAlign: "left",
                      }}
                    >
                      {item.question}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    bgcolor: alpha("#f5f5f5", 0.5),
                    py: { xs: 2, md: 3 },
                    px: { xs: 2, md: 4 },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      color: "text.primary",
                      lineHeight: 1.8,
                      textAlign: "justify",
                      hyphens: "auto",
                    }}
                  >
                    {item.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </motion.div>
          ))}
        </Box>

        {/* Допълнителна информация */}
        <Card
          elevation={2}
          sx={{
            mb: { xs: 4, md: 6 },
            borderRadius: 3,
            background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={2} sx={{ textAlign: "center" }}>
                <Help
                  sx={{
                    fontSize: { xs: 40, md: 50 },
                    color: "#1976d2",
                  }}
                />
              </Grid>
              <Grid item xs={12} md={10}>
                <Typography
                  variant={isMobile ? "subtitle1" : "h6"}
                  sx={{
                    mb: 1,
                    color: "#1a237e",
                    fontWeight: 600,
                    textAlign: { xs: "center", md: "left" },
                  }}
                >
                  Не намирате отговора на вашия въпрос?
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#455a64",
                    mb: 3,
                    textAlign: { xs: "center", md: "justify" },
                    hyphens: "auto",
                  }}
                >
                  Ако вашият въпрос не е отразен в този раздел или имате нужда
                  от допълнителна информация, моля използвайте формата за
                  обратна връзка за да се свържете с екипа ни.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: { xs: "center", md: "flex-start" },
                  }}
                >
                  <Button
                    component={Link}
                    to="/feedback"
                    variant="contained"
                    startIcon={<Feedback />}
                    size={isMobile ? "small" : "medium"}
                    sx={{
                      bgcolor: "#1976d2",
                      "&:hover": { bgcolor: "#1565c0" },
                      borderRadius: 2,
                      px: { xs: 3, md: 4 },
                    }}
                  >
                    Свържете се с нас
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Бутони за навигация */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: { xs: 4, md: 6 },
            gap: { xs: 2, md: 3 },
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <HomeRefreshButton
            label="Към интерактивната карта"
            icon={<Map />}
            centered={false}
            size={isMobile ? "medium" : "large"}
            variant="contained"
            color="primary"
            to="/"
          />

          <HomeRefreshButton
            label="Методология"
            icon={<Info />}
            to="/methodology"
            centered={false}
            size={isMobile ? "medium" : "large"}
            variant="outlined"
            color="success"
          />

          <HomeRefreshButton
            label="За проекта"
            icon={<School />}
            to="/about"
            centered={false}
            size={isMobile ? "medium" : "large"}
            variant="outlined"
            color="secondary"
          />
        </Box>

        {/* Footer информация */}
        <Box
          sx={{
            mt: 6,
            p: 3,
            bgcolor: "grey.50",
            borderRadius: 3,
            textAlign: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              fontStyle: "italic",
              lineHeight: 1.6,
              textAlign: "justify",
              hyphens: "auto",
            }}
          >
            FAQ разделът е съставен на базата на най-често задаваните въпроси от
            потребители и партньори. Информацията се актуализира редовно, за да
            отразява последните промени и разработки в платформата.
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              display: "block",
              mt: 2,
              fontStyle: "italic",
            }}
          >
            Последна актуализация: декември 2025 г.
          </Typography>
        </Box>
      </motion.div>
    </Container>
  );
};

export default Faq;
